import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/archive', label: 'Archive' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/admin/login', label: 'Admin' },
];

const SUBSCRIPTION_PLANS = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 7,
    period: '/month',
    blurb: 'A little something every month to keep the photos coming — and free for everyone.'
  },
  {
    id: 'annual',
    name: 'Annual',
    price: 70,
    period: '/year',
    blurb: "A year's support in one go. Works out cheaper than monthly."
  }
];

function Support() {
  const [email, setEmail] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [donationAmount, setDonationAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDonation, setShowDonation] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);

    try {
      // Paid subscription - redirect to Stripe
      const response = await axios.post(`${BACKEND_URL}/api/supporters/checkout`, {
        email,
        plan: selectedPlan,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDonation = async (e) => {
    e.preventDefault();

    const amount = parseFloat(donationAmount);
    if (!amount || amount < 1) {
      toast.error('Please enter a donation amount of at least $1');
      return;
    }

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/supporters/donate`, {
        email,
        amount,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process donation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const supportButtonLabel = () => {
    if (loading) return 'Processing...';
    return selectedPlan === 'annual' ? 'Support Annually' : 'Support Monthly';
  };

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} />

      {/* Support Hero */}
      <section style={{ textAlign: 'center', padding: '60px 20px 40px', background: 'linear-gradient(135deg, #fff5f5 0%, #ffe6e6 100%)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Heart size={48} style={{ color: '#dc3545', marginBottom: '20px' }} />
          <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '42px', margin: '0 0 15px', color: '#1a1a1a' }}>Support the OBX Beach Bum</h1>
          <p style={{ fontSize: '18px', color: '#666', margin: 0 }}>My photos have always been free. They still are. Support the work if the spirit moves you.</p>
        </div>
      </section>

      {/* Subscription Plans */}
      <main className="support-main" style={{ maxWidth: '1000px', margin: '0 auto', padding: '60px 20px' }}>
        <div
          data-testid="support-plans-grid"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '30px' }}
        >
          {SUBSCRIPTION_PLANS.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              style={{
                background: '#fff',
                border: selectedPlan === plan.id ? '2px solid #5BA4D9' : '1px solid #ddd',
                borderRadius: '10px',
                padding: '20px 15px',
                cursor: 'pointer',
                textAlign: 'left',
                position: 'relative',
                transition: 'border-color 0.2s'
              }}
              data-testid={`plan-${plan.id}`}
            >
              {/* Radio indicator */}
              <div style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: selectedPlan === plan.id ? 'none' : '2px solid #ccc',
                background: selectedPlan === plan.id ? '#5BA4D9' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {selectedPlan === plan.id && (
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#fff'
                  }} />
                )}
              </div>

              {/* Plan Name */}
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                margin: '0 0 5px',
                color: '#1a1a1a',
                paddingRight: '30px'
              }}>
                {plan.name}
              </h3>

              {/* Price */}
              <p style={{
                fontSize: '14px',
                color: '#666',
                margin: '0 0 15px'
              }}>
                ${plan.price}{plan.period}
              </p>

              {/* Blurb */}
              <p style={{
                fontSize: '13px',
                color: '#555',
                margin: 0,
                lineHeight: '1.4'
              }}>
                {plan.blurb}
              </p>
            </div>
          ))}
        </div>

        {/* Email & Subscribe Form */}
        <div className="subscribe-form-container">
          <form onSubmit={handleSubscribe} className="subscribe-form">
            <div className="form-row">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="email-input"
                data-testid="support-email-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="subscribe-btn"
                data-testid="support-checkout-btn"
              >
                {supportButtonLabel()}
              </Button>
            </div>
          </form>

          <div className="support-info">
            <p>✓ Cancel anytime &nbsp;•&nbsp; ✓ Secure payment via Stripe &nbsp;•&nbsp; ✓ All content remains free for everyone</p>
          </div>
        </div>

        {/* One-time Donation Section */}
        <div className="donation-section">
          <div className="donation-header" onClick={() => setShowDonation(!showDonation)}>
            <Gift size={24} />
            <span>Prefer a one-time donation?</span>
            <span className="toggle-arrow">{showDonation ? '▲' : '▼'}</span>
          </div>

          {showDonation && (
            <form onSubmit={handleDonation} className="donation-form">
              <p>Send a one-time tip of any amount to show your support.</p>
              <div className="donation-row">
                <div className="donation-input-wrapper">
                  <span className="currency-symbol">$</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={donationAmount}
                    onChange={(e) => setDonationAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="donation-input"
                    data-testid="donation-amount-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !donationAmount}
                  variant="outline"
                  data-testid="donate-btn"
                >
                  {loading ? 'Processing...' : 'Send Donation'}
                </Button>
              </div>
              <div className="quick-amounts">
                {[5, 10, 25, 50].map(amount => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setDonationAmount(amount.toString())}
                    className={`quick-amount ${donationAmount === amount.toString() ? 'selected' : ''}`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </form>
          )}
        </div>
      </main>

      {/* Footer */}
      <Footer links={FOOTER_LINKS} />
    </div>
  );
}

export default Support;
